import React, { FunctionComponent, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useMedia, createMediaMatcher } from 'react-media-match';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { formatCurrency } from '@empiriecom/module-components/FormattedCurrency';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import { FragmentConfiguration } from '@/config';
import { TippBadge } from '@/src/components/utils';
import { useInstallmentCalculatorContext } from '../../InstallmentCalculatorContext';
import { Value } from '../../types';

const messages = defineMessages({
  badgeText: {
    defaultMessage: 'TIPP',
    id: 'installment-calculator.badge-text',
  },
});

const ElementContainer = styled.div<{ mobile: boolean; isTablet: boolean }>`
  position: relative;
  margin: ${({ mobile }) => (mobile ? '1.25rem 0.75rem;' : '1.25rem 0;')};
  ${({ isTablet }) => isTablet && 'margin-right: 0.75rem;'};
  padding: 0.5rem 0;
  border: 0.0625rem solid #c5c5c5;
  border-radius: 5px;
  cursor: default;
`;

const TippBadgeWrapper = styled.div`
  position: absolute;
  top: -0.5rem;
  left: 50%;
  transform: translateX(-50%);
  background: #fff;
  padding: 0 8px;
`;

const InstallmentInfoContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-flow: row wrap;
  align-items: center;
  padding-bottom: 0.5rem;
  border-bottom: 0.0625rem solid #c5c5c5;
`;

const InstallmentDataContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-flow: column wrap;
  align-items: center;
  width: 50%;
`;

const InstallmentDataLabel = styled.span`
  color: #282929;
`;

const InstallmentShortLabel = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  font-weight: bold;
  font-size: 1rem;
  margin: 0.5rem 0;
`;

const InstallmentDataValue = styled.span`
  font-size: 1.5rem;
  color: #000;
`;

const InstallmentCalculatorLink = styled.a<{ isLargeText: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  line-height: ${({ isLargeText }) => (isLargeText ? 'normal;' : '1.1rem;')};
  text-decoration: underline;
  padding-top: 0.5rem;
  margin: 0;
  width: 100%;
  text-align: center;
  color: #282829;
  ${({ isLargeText }) => isLargeText && 'font-size: 0.9rem;font-weight:bold;'}
`;

const formatFunctions = {
  label: (txt: React.ReactNode) => <InstallmentDataLabel>{txt}</InstallmentDataLabel>,
  value: (txt: React.ReactNode) => <InstallmentDataValue>{txt}</InstallmentDataValue>,
};

export const DefaultElement: FunctionComponent<{
  onClick: () => void;
}> = ({ onClick }) => {
  const { response } = useInstallmentCalculatorContext();
  const [currency, setCurrency] = useState<string>();
  const [singleInstallment, setSingleInstallment] = useState<Value>();
  const [numberOfInstallments, setNumberOfInstallments] = useState<number>();
  const [isFirstUpdate, setIsFirstUpdate] = useState<boolean>(true);
  const intl = useIntl();
  const orientation = createMediaMatcher({
    landscape: '(orientation: landscape) and (max-width : 1026px)',
    portrait: '(orientation: portrait) and (min-width : 766px) and (max-device-width : 1026px)',
  });
  const isTablet = useMedia({
    desktop: false,
    mobile: false,
    tablet: orientation.useMedia({ landscape: true, portrait: true }),
  });
  const mobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const {
    installmentCalculator: { useExtendedLabel, useLargeLinkText },
  } = useConfig<FragmentConfiguration>();

  useEffect(() => {
    if (isFirstUpdate || response.type === 'FIX') {
      setCurrency(response?.currency);
      setSingleInstallment(response?.singleInstallment);
      setNumberOfInstallments(response?.numberOfInstallments);
    }
    setIsFirstUpdate(false);
  }, [response]);

  return currency && singleInstallment && numberOfInstallments ? (
    <ElementContainer
      data-testid="default-installment-calculator-element"
      mobile={mobile}
      onClick={() => onClick()}
      isTablet={isTablet}
    >
      <TippBadgeWrapper>
        <TippBadge text={intl.formatMessage(messages.badgeText)} />
      </TippBadgeWrapper>
      <InstallmentInfoContainer>
        {useExtendedLabel ? (
          <>
            <InstallmentDataContainer>
              <FormattedMessage
                id="installment-calculator.from-value"
                defaultMessage="<label>oder ab </label><value>{data}</value>"
                values={{
                  ...formatFunctions,
                  data: formatCurrency(currency, singleInstallment?.value * 100, intl),
                }}
              />
            </InstallmentDataContainer>
            <InstallmentDataContainer>
              <FormattedMessage
                id="installment-calculator.installments"
                defaultMessage="<label>in bequemen </label><value>{data} Raten</value>"
                values={{
                  ...formatFunctions,
                  data: numberOfInstallments,
                }}
              />
            </InstallmentDataContainer>
          </>
        ) : (
          <InstallmentShortLabel>
            <FormattedMessage
              id="installment-calculator.short-label"
              defaultMessage="oder ab {data} monatlich"
              values={{
                data: formatCurrency(currency, singleInstallment?.value * 100, intl),
              }}
            />
          </InstallmentShortLabel>
        )}
      </InstallmentInfoContainer>
      <InstallmentCalculatorLink isLargeText={useLargeLinkText}>
        <FormattedMessage
          id="installment-calculator.box-link"
          defaultMessage="Wunschrate berechnen"
        />
      </InstallmentCalculatorLink>
    </ElementContainer>
  ) : (
    <></>
  );
};

export default DefaultElement;
